import { useState } from 'react';
import { Funnel } from '@phosphor-icons/react';
import { Button } from '@/components/ui-kit/button';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { Label } from '@/components/ui-kit/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui-kit/popover';
import type { ContactsRestFilters } from '@/modules/contacts/types/contacts-api.types';

/**
 * The filters `GET /v1/lists/{id}/contacts` accepts — the same `EmailStatus`/`SmsStatus` as
 * `GET /v1/contacts`, plus `Source`, which only this endpoint takes. No list filter (the list is
 * the route) and no "Suppressed" (that is `GET /v1/contacts`'s `?suppressed=`).
 */
const FILTER_GROUPS = [
  {
    key: 'emailStatus',
    title: 'Email status',
    options: [
      { value: 'subscribed', label: 'Subscribed' },
      { value: 'pending', label: 'Pending' },
      { value: 'unsubscribed', label: 'Unsubscribed' },
    ],
  },
  {
    key: 'smsStatus',
    title: 'SMS status',
    options: [
      { value: 'subscribed', label: 'Subscribed' },
      { value: 'pending', label: 'Pending' },
      { value: 'unsubscribed', label: 'Unsubscribed' },
    ],
  },
  {
    key: 'source',
    title: 'Source',
    options: [
      { value: 'manual', label: 'Manual' },
      { value: 'api', label: 'API' },
      { value: 'import', label: 'Import' },
    ],
  },
] as const;

export type ListMembersFilters = Pick<ContactsRestFilters, 'emailStatus' | 'smsStatus' | 'source'>;

type FilterKey = (typeof FILTER_GROUPS)[number]['key'];

const toggleValue = (values: string[], value: string, checked: boolean): string[] => {
  if (checked) return values.includes(value) ? values : [...values, value];
  return values.filter((entry) => entry !== value);
};

export const countActiveListMembersFilters = (filters: ListMembersFilters): number =>
  (filters.emailStatus?.length ?? 0) + (filters.smsStatus?.length ?? 0) + (filters.source?.length ?? 0);

type ListMembersFiltersDropdownProps = {
  filters: ListMembersFilters;
  onApply: (filters: ListMembersFilters) => void;
};

export const ListMembersFiltersDropdown = ({ filters, onApply }: ListMembersFiltersDropdownProps) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<ListMembersFilters>(filters);
  const activeCount = countActiveListMembersFilters(filters);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) setDraft(filters);
    setOpen(nextOpen);
  };

  const handleDone = () => {
    onApply(draft);
    setOpen(false);
  };

  const setGroup = (key: FilterKey, values: string[]) =>
    setDraft((prev) => ({ ...prev, [key]: values }));

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="h-10 shrink-0 gap-2 px-4 text-sm font-medium">
          <Funnel className="size-4 shrink-0" aria-hidden />
          Filters
          {activeCount > 0 ? (
            <span className="ml-0.5 rounded-full bg-primary-50 px-1.5 text-xs font-medium text-primary-600">
              {activeCount}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="flex w-72 max-w-[calc(100vw-2rem)] flex-col overflow-hidden p-0">
        <div className="flex-1 overflow-y-auto p-4">
          {FILTER_GROUPS.map((group) => {
            const selected = draft[group.key] ?? [];
            return (
              <div key={group.key} className="mb-3 last:mb-0">
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {group.title}
                </p>
                <div className="flex flex-col gap-2">
                  {group.options.map((option) => {
                    const id = `list-members-filter-${group.key}-${option.value}`;
                    return (
                      <div key={option.value} className="flex items-center gap-2">
                        <Checkbox
                          id={id}
                          checked={selected.includes(option.value)}
                          onCheckedChange={(checked) =>
                            setGroup(group.key, toggleValue(selected, option.value, checked === true))
                          }
                        />
                        <Label htmlFor={id} className="cursor-pointer text-sm font-normal">
                          {option.label}
                        </Label>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex shrink-0 items-center justify-between border-t bg-popover px-4 py-3">
          <button
            type="button"
            className="text-xs font-medium text-muted-foreground hover:text-foreground"
            onClick={() => setDraft({ emailStatus: [], smsStatus: [], source: [] })}
          >
            Clear all
          </button>
          <button
            type="button"
            className="text-xs font-medium text-primary-600 hover:text-primary-700"
            onClick={handleDone}
          >
            Done
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
